import { Link } from 'react-router-dom';
import { useState } from 'react';
import { Button } from '../../components/ui/button';
import { ScanLine, X, Check, AlertTriangle, User, MapPin, Timer, Trophy, ShieldCheck, Camera, CameraOff } from 'lucide-react';
import { api } from '../../lib/supabase';
import { QrScanner } from '../../components/common/QrScanner';

export default function Scanner() {
  const [state, setState] = useState('scanning'); // scanning | valid | invalid
  const [reason, setReason] = useState('');
  const [runner, setRunner] = useState(null);
  const [busy, setBusy] = useState(false);
  const [cameraError, setCameraError] = useState(null);

  async function attemptScan(qrToken) {
    if (busy) return;
    const eventId = sessionStorage.getItem('rivl:scanner:eventId') || 'evt_001';
    setBusy(true);
    try {
      const result = await api.scanMedal(
        qrToken,
        eventId,
        sessionStorage.getItem('rivl:scanner:name') || 'Scanner'
      );
      if (result === 'valid') {
        let info = null;
        try {
          info = await api.getRunnerByQR(qrToken, eventId);
        } catch {
          /* runner card lookup is best-effort */
        }
        setRunner(info || { token: qrToken });
        setState('valid');
      } else {
        const labels = {
          invalid_token: 'INVALID TOKEN',
          already_used: 'TOKEN ALREADY USED',
          not_verified: 'NOT VERIFIED',
          wrong_event: 'WRONG EVENT',
        };
        setReason(labels[result] || 'INVALID');
        setState('invalid');
      }
    } catch {
      setReason('SCAN ERROR');
      setState('invalid');
    } finally {
      setBusy(false);
    }
  }

  function dismiss() {
    setState('scanning');
    setRunner(null);
    setReason('');
  }

  if (state === 'valid') {
    return <ValidPanel runner={runner} onDismiss={dismiss} />;
  }

  if (state === 'invalid') {
    return (
      <div className="fixed inset-0 bg-destructive text-destructive-foreground flex flex-col items-center justify-center z-50" data-testid="scanner-invalid">
        <AlertTriangle className="h-32 w-32 stroke-[3]" />
        <h1 className="font-display text-5xl md:text-6xl font-black tracking-tight mt-4">{reason}</h1>
        <p className="mt-3 text-base opacity-80">Hand back to runner. Direct to support.</p>
        <Button onClick={dismiss} variant="outline" className="mt-8 h-11 rounded-md bg-transparent border-destructive-foreground/40 text-destructive-foreground" data-testid="scanner-dismiss-invalid">
          Scan next
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-foreground text-background flex flex-col" data-testid="scanner-active">
      <div className="flex items-center justify-between px-4 py-4">
        <div>
          <div className="rivl-label text-[10px] opacity-70">Scanner</div>
          <div className="font-display text-base font-semibold">Active scan</div>
        </div>
        <Button asChild variant="ghost" size="icon" className="text-background hover:bg-background/10" data-testid="scanner-exit">
          <Link to="/create"><X className="h-5 w-5" /></Link>
        </Button>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <div className="relative aspect-square w-full max-w-sm border-2 border-primary rounded-md overflow-hidden bg-black">
          {/* Live camera feed renders here */}
          <div id="rivl-qr-reader" className="absolute inset-0 [&_video]:object-cover [&_video]:w-full [&_video]:h-full" data-testid="qr-camera" />
          {!cameraError && (
            <QrScanner
              containerId="rivl-qr-reader"
              active={state === 'scanning' && !busy}
              onDetect={attemptScan}
              onError={(err) => setCameraError(err.message || String(err))}
            />
          )}

          {/* overlays */}
          <div className="pointer-events-none absolute inset-0">
            <div className="absolute top-0 left-0 right-0 h-1 bg-primary animate-pulse" />
            {cameraError && (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4 bg-black/85">
                <CameraOff className="h-10 w-10 text-primary" />
                <p className="text-xs text-background/80 mt-3 max-w-xs" data-testid="camera-error">
                  {cameraError.includes('Permission')
                    ? 'Camera permission denied. Allow access in your browser settings, then reload.'
                    : `Camera unavailable: ${cameraError}`}
                </p>
                <p className="text-[10px] text-background/50 mt-2">Use the demo buttons below to test.</p>
              </div>
            )}
            {!cameraError && (
              <div className="absolute inset-0 flex items-center justify-center">
                <ScanLine className="h-16 w-16 text-primary/40" />
              </div>
            )}
            <div className="absolute top-2 left-2 h-6 w-6 border-t-2 border-l-2 border-primary" />
            <div className="absolute top-2 right-2 h-6 w-6 border-t-2 border-r-2 border-primary" />
            <div className="absolute bottom-2 left-2 h-6 w-6 border-b-2 border-l-2 border-primary" />
            <div className="absolute bottom-2 right-2 h-6 w-6 border-b-2 border-r-2 border-primary" />
          </div>
        </div>
      </div>

      <div className="px-4 py-4 space-y-3">
        <p className="text-center text-[11px] opacity-60 flex items-center justify-center gap-1">
          <Camera className="h-3 w-3" /> Point camera at runner's medal QR
        </p>
        <div className="grid grid-cols-2 gap-3">
          <Button onClick={() => attemptScan('RIVL-DEMO-VALID')} className="h-10 rounded-md bg-success/90 text-success-foreground hover:bg-success text-xs" data-testid="scanner-demo-valid">
            <Check className="h-3.5 w-3.5 mr-1" /> Demo Valid
          </Button>
          <Button onClick={() => attemptScan('RIVL-INVALID-XX')} className="h-10 rounded-md bg-destructive/90 text-destructive-foreground hover:bg-destructive text-xs" data-testid="scanner-demo-invalid">
            <X className="h-3.5 w-3.5 mr-1" /> Demo Invalid
          </Button>
        </div>
      </div>
    </div>
  );
}

function ValidPanel({ runner, onDismiss }) {
  const initials = (runner?.name || 'R').split(' ').map((n) => n[0]).slice(0, 2).join('');
  const adherence = runner?.coverage ?? null;
  return (
    <div className="fixed inset-0 z-50 flex flex-col" data-testid="scanner-valid">
      {/* Hero confirmation */}
      <div className="bg-success text-success-foreground py-8 flex flex-col items-center">
        <Check className="h-20 w-20 stroke-[3]" />
        <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight mt-2">MEDAL CONFIRMED</h1>
      </div>

      {/* Runner info card */}
      <div className="flex-1 bg-background overflow-y-auto px-4 py-5 space-y-4">
        {runner?.name ? (
          <>
            <div className="flex items-center gap-4">
              {runner.avatarUrl ? (
                <img
                  src={runner.avatarUrl}
                  alt={runner.name}
                  className="h-16 w-16 rounded-md object-cover border border-border"
                />
              ) : (
                <div className="h-16 w-16 rounded-md bg-secondary flex items-center justify-center text-lg font-bold">
                  {initials}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="font-display text-xl font-bold truncate" data-testid="scan-runner-name">{runner.name}</div>
                <div className="font-mono text-xs text-muted-foreground mt-0.5">
                  Bib <span className="text-foreground font-semibold" data-testid="scan-runner-bib">{runner.bib}</span>
                </div>
                {runner.verified && (
                  <div className="inline-flex items-center gap-1 mt-1 text-[10px] uppercase tracking-widest text-success font-semibold">
                    <ShieldCheck className="h-3 w-3" /> Verified finisher
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2">
              <StatTile
                icon={Timer}
                label="Finish time"
                value={runner.finishTime || '—'}
                testid="scan-runner-time"
              />
              <StatTile
                icon={Trophy}
                label="Rank"
                value={runner.rank ? `#${runner.rank}` : '—'}
                testid="scan-runner-rank"
              />
              <StatTile
                icon={MapPin}
                label="Pace / km"
                value={runner.paceMinPerKm || '—'}
                testid="scan-runner-pace"
              />
            </div>

            {adherence !== null && (
              <div className="rivl-card p-4 space-y-2" data-testid="scan-runner-adherence">
                <div className="flex items-center justify-between">
                  <span className="rivl-label">Track adherence</span>
                  <span className="font-mono font-bold text-sm">{adherence}%</span>
                </div>
                <div className="h-2 w-full bg-secondary rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${adherence >= 95 ? 'bg-success' : adherence >= 70 ? 'bg-primary' : 'bg-destructive'}`}
                    style={{ width: `${Math.max(2, Math.min(100, adherence))}%` }}
                  />
                </div>
                <p className="text-[11px] text-muted-foreground">
                  {adherence >= 95
                    ? 'Stayed on the official route.'
                    : adherence >= 70
                      ? 'Some deviation — under review.'
                      : 'Significant deviation from the route.'}
                </p>
              </div>
            )}
          </>
        ) : (
          <div className="rivl-card p-5 flex items-center gap-3">
            <User className="h-5 w-5 text-muted-foreground" />
            <div className="text-sm text-muted-foreground">
              Token validated. Runner profile unavailable.
            </div>
          </div>
        )}

        <Button
          onClick={onDismiss}
          className="w-full h-12 rounded-md text-base font-semibold mt-2"
          data-testid="scan-next-btn"
        >
          Scan next runner
        </Button>
      </div>
    </div>
  );
}

function StatTile({ icon: Icon, label, value, testid }) {
  return (
    <div className="rivl-card p-3 text-center" data-testid={testid}>
      <Icon className="h-4 w-4 mx-auto text-muted-foreground" />
      <div className="rivl-label text-[9px] mt-1">{label}</div>
      <div className="font-mono font-bold text-sm mt-1 truncate">{value}</div>
    </div>
  );
}
